import { RegisterType, getRegisterTypeText } from './enums';
import { PANEL_IDS } from '@/constants/panelIds';
import permissions from '@Share/config/permissions';

export const REGISTER_VIEWER_ACTION = 'show-register-tags';
// The Inspection search endpoints interpret an empty ID array as "no ID filter". Keep an active
// zero-result Register filter fail-closed by sending one impossible, centralized string ID instead.
export const REGISTER_FILTER_EMPTY_TAG_ID = '__register_filter_no_matching_tag__';

const OPERATION_PANEL_TYPES = Object.freeze([
  RegisterType.General,
  RegisterType.Scaffold,
  RegisterType.Permit,
  RegisterType.Container,
  RegisterType.Operation,
]);

const TYPE_CONTEXT = Object.freeze({
  [RegisterType.General]: {
    name: 'General',
    panelId: PANEL_IDS.TAGS_OPERATION,
    permissions: [permissions.Tags.General.View],
  },
  [RegisterType.Equipment]: {
    name: 'Equipment',
    panelId: PANEL_IDS.TAGS_EQUIPMENT,
    permissions: [permissions.Tags.Equipment.View],
  },
  [RegisterType.Pipeline]: {
    name: 'Pipeline',
    panelId: PANEL_IDS.TAGS_PIPELINE,
    permissions: [permissions.Tags.Pipeline.View],
  },
  [RegisterType.Structural]: {
    name: 'Structural',
    panelId: PANEL_IDS.TAGS_STRUCTURAL,
    permissions: [permissions.Tags.Structural.View],
  },
  [RegisterType.Inspection]: {
    name: 'Inspection',
    panelId: PANEL_IDS.CONDITION_MAP,
    inspectionTab: 0,
    permissions: [permissions.InspectionTags.View],
  },
  [RegisterType.Anomaly]: {
    name: 'Anomaly',
    panelId: PANEL_IDS.TAGS_ANOMALY,
    permissions: [permissions.Tags.Anomaly.View],
  },
  [RegisterType.AvaInspect]: {
    name: 'AvaInspect',
    panelId: PANEL_IDS.CONDITION_MAP,
    inspectionTab: 1,
    permissions: [permissions.Tags.AvaInspect.View],
  },
  [RegisterType.Scaffold]: {
    name: 'Scaffold',
    panelId: PANEL_IDS.TAGS_OPERATION,
    permissions: [permissions.Tags.Scaffold.View],
  },
  [RegisterType.Permit]: {
    name: 'Permit',
    panelId: PANEL_IDS.TAGS_OPERATION,
    permissions: [permissions.Tags.Permit.View],
  },
  [RegisterType.Container]: {
    name: 'Container',
    panelId: PANEL_IDS.TAGS_OPERATION,
    permissions: [permissions.Tags.Container.View],
  },
  [RegisterType.Operation]: {
    name: 'Operation',
    panelId: PANEL_IDS.TAGS_OPERATION,
    permissions: [
      permissions.Tags.General.View,
      permissions.Tags.Scaffold.View,
      permissions.Tags.Permit.View,
      permissions.Tags.Container.View,
    ],
  },
});

export function normalizeRegisterType(value) {
  if (typeof value === 'number' && TYPE_CONTEXT[value]) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const numeric = Number(value);
    if (TYPE_CONTEXT[numeric]) return numeric;
  }

  const normalized = String(value || '').replace(/[\s_-]+/g, '').toLowerCase();
  if (normalized === 'annotation') return RegisterType.General;
  if (normalized === 'avainspection') return RegisterType.AvaInspect;
  return Object.values(RegisterType).find((type) =>
    String(TYPE_CONTEXT[type]?.name || '').toLowerCase() === normalized
  ) || null;
}

export function getRegisterViewerContext(value) {
  const registerType = normalizeRegisterType(value);
  if (!registerType) return null;
  return { registerType, ...TYPE_CONTEXT[registerType] };
}

export function isOperationRegisterType(value) {
  const registerType = normalizeRegisterType(value);
  return OPERATION_PANEL_TYPES.includes(registerType);
}

export function getRegisterFilterLabel(value) {
  const registerType = normalizeRegisterType(value);
  return registerType ? `${getRegisterTypeText(registerType)} register filter` : 'Register filter';
}

export function tagBelongsToRegisterPanel(tag, registerTypeValue) {
  const registerType = normalizeRegisterType(registerTypeValue);
  const discriminator = tag?.discriminator;

  if (registerType === RegisterType.Operation) {
    return discriminator === 'ForgeAnnotation' || discriminator === 'ForgeScaffold';
  }
  if (registerType === RegisterType.General) return discriminator === 'ForgeAnnotation';
  if (registerType === RegisterType.Scaffold) {
    return discriminator === 'ForgeScaffold' && (!tag.type || String(tag.type).toLowerCase() === 'scaffold');
  }
  if (registerType === RegisterType.Permit) {
    return discriminator === 'ForgeScaffold' && String(tag.type || '').toLowerCase() === 'permit';
  }
  if (registerType === RegisterType.Container) {
    return discriminator === 'ForgeScaffold' && String(tag.type || '').toLowerCase() === 'container';
  }

  const discriminators = {
    [RegisterType.Equipment]: 'ForgeEquipment',
    [RegisterType.Pipeline]: 'ForgePipeline',
    [RegisterType.Structural]: 'ForgeStructural',
    [RegisterType.Inspection]: 'ForgeInspection',
    [RegisterType.Anomaly]: 'ForgeAnomaly',
    [RegisterType.AvaInspect]: 'ForgeAvaInspect',
  };
  return discriminator === discriminators[registerType];
}

export function getRegisterTagViewPermission(tag) {
  if (tag?.discriminator === 'ForgeAnnotation') return permissions.Tags.General.View;
  if (tag?.discriminator === 'ForgeScaffold') {
    const type = String(tag.type || 'scaffold').trim().toLowerCase();
    if (type === 'scaffold') return permissions.Tags.Scaffold.View;
    if (type === 'permit') return permissions.Tags.Permit.View;
    if (type === 'container') return permissions.Tags.Container.View;
    return null;
  }

  const discriminatorPermissions = {
    ForgeEquipment: permissions.Tags.Equipment.View,
    ForgePipeline: permissions.Tags.Pipeline.View,
    ForgeStructural: permissions.Tags.Structural.View,
    ForgeInspection: permissions.InspectionTags.View,
    ForgeAnomaly: permissions.Tags.Anomaly.View,
    ForgeAvaInspect: permissions.Tags.AvaInspect.View,
  };
  return discriminatorPermissions[tag?.discriminator] || null;
}
